import React, { ReactElement, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import Info from '@material-ui/icons/Info';
import { Box, Button, Divider, Popover, Typography, TypographyProps } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';

export interface Props extends TypographyProps {
  children?: React.ReactNode;
  PopoverContentProps: PopoverContentProps;
  onOpen?: () => void;
  onClose?: () => void;
}

interface PopoverContentProps {
  title?: string;
  image?: string;
  centerIcon?: boolean;
  description?: React.ReactNode;
  link?: string;
  classes?: Record<string, string>;
}

interface InfoButtonProps {
  className?: string;
  onClick: (event: React.MouseEvent) => void;
  open: boolean;
}

const getTextComponents = (text: string): string[] => {
  const result = text.trim().split(/(\s+)/);
  const lastWord = result.pop();

  return lastWord ? [result.join(''), lastWord] : [text];
};

const InfoButton = (props: InfoButtonProps): ReactElement => (
  <Box
    onClick={props.onClick}
    aria-owns={props.open ? 'mouse-over-popover' : undefined}
    aria-haspopup="true"
    displayPrint="none"
    display="inline"
    style={{ paddingLeft: 5, verticalAlign: 'middle' }}
  >
    <Info className={props.className} />
  </Box>
);

const PopoverContent = ({
  title,
  image,
  description,
  link,
  onClick,
  t,
  classes
}: PopoverContentProps & {
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  t: TFunction;
}): ReactElement => (
  <Fragment>
    {title && (
      <Typography className={classes?.popoverContentTitle} variant="h5">
        <Info className={classes?.popoverContentTitleIcon} /> {title}
      </Typography>
    )}
    {image && <img src={image} alt={title} className={classes?.popoverContentImage} />}
    {description && (
      <Typography component="div" variant="body1" className={classes?.popoverContentDescription}>
        {description}
      </Typography>
    )}
    <Divider />
    <div className={classes?.popoverContentFooter}>
      {link && (
        <Button
          aria-label={t('Learn More')}
          variant="text"
          size="small"
          disableElevation
          href={link}
        >
          {t('Learn More')}
        </Button>
      )}
      <Button
        aria-label={t('Dismiss')}
        variant="contained"
        color="primary"
        size="small"
        disableElevation
        onClick={onClick}
      >
        {t('Dismiss')}
      </Button>
    </div>
  </Fragment>
);

/*
   The intent of the InlineTextPopover vs Popover is to provide an information popover for a Typography/text component where the
   icon to trigger the popover doesn't wrap independently of the text, making it look goofy.

   There might be a better way (i.e. css-only) to force the icon to always wrap with the last word of the text, but doing some
   ugly string-splitting, nowrap stuff for now.
*/
const InlineTextPopover = ({
  children,
  onOpen,
  onClose,
  PopoverContentProps,
  ...typographyProps
}: Props): ReactElement => {
  const classes = useStyles();
  const { t } = useTranslation('Tooltips');
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const open = !!anchorEl;

  const handlePopoverOpen = (event: React.MouseEvent) => {
    setAnchorEl(event.currentTarget);
    onOpen?.();
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    onClose?.();
  };

  let body = children;
  if (typeof children === 'string') {
    const [words, noWrapWord] = getTextComponents(children);
    body = (
      <>
        {words}
        <span aria-hidden="true" style={{ whiteSpace: 'nowrap' }}>
          {noWrapWord}
          <InfoButton open={open} onClick={handlePopoverOpen} className={classes.infoButtonIcon} />
        </span>
      </>
    );
  } else {
    body = (
      <>
        {children}
        <InfoButton open={open} onClick={handlePopoverOpen} className={classes.infoButtonIcon} />
      </>
    );
  }

  return (
    <Typography {...typographyProps}>
      {body}
      <Popover
        id="mouse-over-popover"
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={handlePopoverClose}
        onMouseLeave={handlePopoverClose}
        disableRestoreFocus
        classes={{ paper: classes.popoverContentPaper }}
      >
        <PopoverContent
          {...PopoverContentProps}
          onClick={handlePopoverClose}
          t={t}
          classes={classes}
        />
      </Popover>
    </Typography>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  infoButtonIcon: {
    color: theme.palette.secondary.main,
    fontSize: theme.typography.pxToRem(20),
    cursor: 'pointer'
  },
  popoverContentPaper: {
    marginTop: theme.spacing(1.5),
    borderRadius: 0,
    maxWidth: 300,
    overflow: 'visible',
    '&:before': {
      content: '""',
      width: 0,
      height: 0,
      left: 0,
      right: 0,
      top: theme.spacing(-1.5),
      position: 'absolute',
      margin: 'auto',
      borderLeft: '12px solid transparent',
      borderRight: '12px solid transparent',
      borderBottom: '12px solid #fff',
      filter: 'drop-shadow(-1px -1px 1px rgba(0,0,0,.2))'
    }
  },
  popoverContentTitle: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2)
  },
  popoverContentTitleIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.secondary.main,
    fontSize: theme.typography.pxToRem(26)
  },
  popoverContentImage: { width: 300 },
  popoverContentDescription: { padding: `${theme.spacing(1)}px ${theme.spacing(2)}px` },
  popoverContentFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(1)
  }
}));

export default InlineTextPopover;
